import smartDate from "@qrohlf/smart-date";
import cx from "classnames";
import { CommentsContainer } from "common/Comments";
import { CoreLink } from "common/CoreLink";
import { Icon } from "common/Icon";
import { RouteThumb, TripThumb } from "common/ListItems";
import { ProfileImg } from "common/ProfileImg";
import { ShareDialog, SHARE_DIALOG } from "common/ShareDialog";
import { CTA } from "common/styleguide/CTA";
import { Topo } from "common/styleguide/Topo";
import { Distance, Duration, Elevation, Grade, Speed } from "common/units";
import { Trip } from "data/records/Trip";
import { photoSelectors } from "data/resources/photos";
import { galleryMessages } from "features/gallery/Gallery";
import { showGallery } from "features/gallery/showGallery";
import { commonMetrics, commonNouns, Msg, useFormatMessage } from "lib/localization";
import { j } from "lib/textUtils";
import { photoGear } from "lib/urlHelpers";
import { urls } from "lib/urls";
import { userIsAdmin } from "lib/utils";
import _ from "lodash";
import moment from "moment";
import React, { Fragment, useState } from "react";
import { findDOMNode } from "react-dom";
import { defineMessages, FormattedMessage as T } from "react-intl";
import { connect } from "react-redux";
import llUpsell from "./images/ll-upsell-bg.png";
import llUpsell2x from "./images/ll-upsell-bg@2x.png";
import llUpsell3x from "./images/ll-upsell-bg@3x.png";
import styles from "./SidePanel.scss";

const llSpMessages = defineMessages({
  vPoor: {
    id: "llSpMessages.vPoor",
    defaultMessage: "Very Poor",
  },
  poor: {
    id: "llSpMessages.poor",
    defaultMessage: "Poor",
  },
  ok: {
    id: "llSpMessages.ok",
    defaultMessage: "OK",
  },
  good: {
    id: "llSpMessages.good",
    defaultMessage: "Good",
  },
  excellent: {
    id: "llSpMessages.excellent",
    defaultMessage: "Excellent",
  },
  signalStrength: {
    id: "llSpMessages.signalStrength",
    defaultMessage: "Signal Strength",
  },
  battery: {
    id: "llSpMessages.battery",
    defaultMessage: "Battery",
  },
  commentPlaceholder: {
    id: "llSidePanel.commentPlaceholder",
    defaultMessage: "Add a comment, and it will be read aloud in real time!",
  },
  distanceTraveled: {
    id: "llSidePanel.distanceTraveled",
    defaultMessage: "Distance Traveled",
  },
});

export class SidePanel extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      scrollTop: 0,
      headerHeight: 9999,
      collapseHeight: 0,
    };
  }

  componentDidMount() {
    window.addEventListener("resize", this.throttledOnResize);
    this.onResize();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.throttledOnResize);
  }

  onResize = () => {
    this.setState({
      collapseHeight: findDOMNode(this._stickyHeader).scrollHeight,
    });
  };

  throttledOnResize = _.throttle(this.onResize, Math.floor(1000 / 60));

  onContentScroll = (e) => {
    this.throttledOnContentScroll(e.target.scrollTop);
  };

  throttledOnContentScroll = _.throttle(
    (scrollTop) =>
      this.setState({
        scrollTop,
        headerHeight: findDOMNode(this._header).scrollHeight - scrollTop,
      }),
    Math.floor(1000 / 60), // aim for a 60fps header animation
    { leading: true }
  );

  render() {
    const {
      user,
      liveLog,
      trip,
      privacyCode,
      troute,
      currentUser,
      className,
      mobile,
      completed,
      unstarted,
      fullscreen,
      readOnly,
    } = this.props;
    const { headerHeight, collapseHeight } = this.state;
    const showCollapsedHeader = headerHeight <= collapseHeight;
    const finalized = liveLog.finalized;

    const toShow = unstarted ? Trip() : trip;

    return (
      <div className={cx(styles.SidePanel, className, mobile && styles.mobile, fullscreen && styles.fullscreen)}>
        <Topo
          dark
          ref={(r) => {
            this._stickyHeader = r;
          }}
          className={cx(styles.stickyHeader, showCollapsedHeader && styles.stickyHeaderVisible)}
        >
          <Masthead user={user} trip={trip} mini finalized={finalized} />
        </Topo>
        <div className={styles.scrollContent} onScroll={this.onContentScroll}>
          <Topo
            ref={(r) => {
              this._header = r;
            }}
            dark
          >
            <Masthead user={user} trip={trip} finalized={finalized} />
          </Topo>
          <div className={styles.body}>
            <Overview
              liveLog={liveLog}
              trip={trip}
              completed={completed}
              unstarted={unstarted}
              currentUser={currentUser}
            />
            <Photos trip={toShow} />
            {!finalized && trip.id && (
              <CommentSection readOnly={readOnly} trip={trip} privacyCode={privacyCode} currentUser={currentUser} />
            )}
            <RideProgress trip={toShow} />
            {!unstarted && troute && <LinkedTRoute troute={troute} />}
          </div>
          {!currentUser.id && <SignUpsell />}
        </div>
      </div>
    );
  }
}

const Masthead = ({ user, trip, mini, finalized }) => {
  const [dialog, setDialog] = useState(null);

  return (
    <div className={cx(styles.masthead, mini && styles.miniMasthead)}>
      {dialog === SHARE_DIALOG && (
        <ShareDialog
          onRequestClose={() => setDialog(null)}
          objectType="trip"
          objectId={trip.id}
          objectTitle={trip.name}
          overlayTitle={
            <Fragment>
              Share <strong>{user.name}</strong>'s Livelog
            </Fragment>
          }
          privacyCode={trip.privacyCode}
          objectUrl={urls.users.liveLog(trip.id, trip.privacyCode)}
        />
      )}
      {!mini && <ProfileImg sidebarHeader user={user} className={styles.profileImg} />}
      <div className={mini ? styles.miniHeaderText : styles.headerText}>
        {mini ? (
          <a className={styles.aSpecificity} href={urls.users.show(user.id)}>
            {user.name}
          </a>
        ) : (
          <h1>
            <a href={urls.users.show(user.id)}>{user.name}</a>
          </h1>
        )}
        {finalized ? (
          <T id="llSidePanel.isNotLL" defaultMessage="is not currently Live Logging" />
        ) : (
          <T id="llSidePanel.isLL" defaultMessage="is Live Logging" />
        )}
      </div>
      {/* removing livelog share for now 7/9/2019 */}
      {/* <Icon i='md-share' className={styles.shareIcon} onClick={() => setDialog(SHARE_DIALOG)} /> */}
    </div>
  );
};

const Overview = ({ liveLog, trip, completed, unstarted, currentUser }) => {
  const eta = liveLog.eta && moment(liveLog.eta).format("h:mm A");
  const lastUpdate = moment(liveLog.updatedAt).format("h:mm A");
  const { battery, signal } = liveLog;

  return (
    <div className={styles.bodySection}>
      <h3 className={styles.sectionHeader}>
        <Msg m={commonNouns.overview} />
      </h3>
      <hr />
      <div className={styles.overviewMetrics}>
        <OverviewMetric
          value={
            unstarted ? (
              <Duration valueOnly value={0} />
            ) : (
              <span data-tip title={smartDate(liveLog.updatedAt)}>
                {lastUpdate}
              </span>
            )
          }
          label={<T id="llSidePanel.lastUpdate" defaultMessage="Last Update" />}
          i="md-timer"
        />
        {eta && (
          <OverviewMetric
            value={
              <span data-tip title={smartDate(liveLog.eta)}>
                {eta}
              </span>
            }
            label={
              <T
                id="llSidePanel.estCompletionTime"
                defaultMessage="Est. Completion Time"
                description="Est. is an abbreviation for Estimated"
              />
            }
            i="md-time"
          />
        )}
        <OverviewMetric
          value={<Distance short bigUnits value={unstarted ? 0 : trip.distance} />}
          label={<Msg m={llSpMessages.distanceTraveled} />}
          i="distance"
        />
        <SignalMetric signal={signal} />
        <BatteryMetric battery={battery} />
      </div>
    </div>
  );
};

export const OverviewMetric = ({ value, label, i, iconStyle }) => {
  return (
    <div className={styles.overviewMetric}>
      <div className={styles.overviewMetricTop}>
        <Icon i={i} size={20} style={iconStyle} />
        <div className={styles.overviewMetricValue}>{value}</div>
      </div>
      <div className={styles.overviewMetricLabel}>{label}</div>
    </div>
  );
};

const SignalMetric = ({ signal }) => {
  const fm = useFormatMessage();
  const signals = [
    { text: fm(llSpMessages.vPoor), color: "#FF3940" },
    { text: fm(llSpMessages.poor), color: "#FF3940" },
    { text: fm(llSpMessages.ok), color: "#FA6400" },
    { text: fm(llSpMessages.good), color: "#00A55F" },
    { text: fm(llSpMessages.excellent), color: "#00A55F" },
    { text: fm(llSpMessages.excellent), color: "#00A55F" }, // one of the apps apparently sends signal 5?
  ];

  const signalIsValid = _.isInteger(signal) && signal >= 0 && signal < signals.length;
  if (!signalIsValid) {
    return null;
  }

  return (
    <OverviewMetric
      value={signals[signal].text}
      label={fm(llSpMessages.signalStrength)}
      i="md-wifi"
      iconStyle={{ color: signals[signal].color }}
    />
  );
};

const BatteryMetric = ({ battery }) => {
  if (_.isNull(battery)) return null;

  const color = battery > 0.3 ? null : "#FF3940";
  const fm = useFormatMessage();
  return (
    <OverviewMetric
      value={Math.round(battery * 100) + "%"}
      label={fm(llSpMessages.battery)}
      i="ios-battery"
      iconStyle={color && { color }}
    />
  );
};

class _Photos extends React.PureComponent {
  orderedPhotos() {
    const { trip, getPhoto } = this.props;
    const orderedPhotoIds = trip.photoIds.sort((a, b) => a - b);
    return orderedPhotoIds.map((pid) => getPhoto(pid));
  }

  showGalleryWithProps(activePhotoId) {
    const { trip } = this.props;

    return showGallery({
      photos: this.orderedPhotos(),
      readonly: true,
      parentType: "trip",
      parentId: trip.id,
      activePhotoId,
    });
  }

  render() {
    const { trip } = this.props;
    const orderedPhotos = this.orderedPhotos();
    return (
      <div className={styles.bodySection}>
        <h3 className={styles.sectionHeader}>
          <Msg capitalized m={commonNouns.photos} values={{ count: 0 }} />
        </h3>
        <hr />
        {orderedPhotos.length === 0 && (
          <div className={styles.noPhotos}>
            <T id="llSidePanel.noPhotosYet" defaultMessage="No photos have been uploaded yet." />
          </div>
        )}
        {orderedPhotos.length > 0 && (
          <Fragment>
            <div className={styles.photoGrid}>
              {orderedPhotos.slice(0, 6).map((p) => (
                <div className={styles.photo} key={p.id}>
                  <img src={photoGear(p.id)} onClick={() => this.showGalleryWithProps(p.id)} />
                </div>
              ))}
            </div>
            <div className={styles.morePhotos}>
              <span>
                <a onClick={() => this.showGalleryWithProps()}>
                  <Msg m={galleryMessages.openGallery} />
                </a>{" "}
                ({trip.photoIds.length} <Msg m={commonNouns.photos} values={{ count: trip.photoIds.length }} />)
              </span>
            </div>
          </Fragment>
        )}
      </div>
    );
  }
}

const photoMapStateToProps = (state, _ownProps) => ({
  getPhoto: (id) => photoSelectors.find(state, id),
});

const Photos = connect(photoMapStateToProps, null)(_Photos);

const CommentSection = ({ trip, privacyCode, currentUser, readOnly }) => {
  const canModerate = trip.userId === currentUser.id || userIsAdmin;
  const fm = useFormatMessage();

  return (
    <div className={styles.bodySection}>
      <h3 className={styles.sectionHeader}>
        <Msg capitalized m={commonNouns.comments} values={{ count: 0 }} />
      </h3>
      <hr />
      <CommentsContainer
        objectType="trip"
        objectId={trip.get("id")}
        privacyCode={privacyCode}
        includeHeading={false}
        canModerate={!readOnly && canModerate}
        readOnly={readOnly}
        className={styles.commentsContainer}
        reverseOrder
        showTime
        placeholder={fm(llSpMessages.commentPlaceholder)}
      />
    </div>
  );
};

const RideProgress = ({ trip }) => {
  const grade = trip && trip.metrics && trip.metrics.grade ? trip.metrics.grade.max : null;
  const stoppedTime = trip && trip.metrics ? trip.metrics.stoppedTime : null;

  return (
    <div className={cx(styles.bodySection, styles.rideProgress)}>
      <h3 className={styles.sectionHeader}>
        <T id="llSidePanel.rideProgress" defaultMessage="Ride Progress" />
      </h3>
      <hr />
      <RideProgressRow
        label={<T id="RideProgress.timeStarted" defaultMessage="Time Started" />}
        value={moment(trip.departedAt).format("MMM D, h:mm A")}
      />
      <hr />
      <RideProgressRow
        label={<Msg m={llSpMessages.distanceTraveled} />}
        value={<Distance short value={trip.distance} />}
      />
      <hr />
      <RideProgressRow
        label={<Msg startCase m={commonMetrics.elevationGain} />}
        value={<Elevation short value={trip.elevationGain} />}
      />
      <hr />
      <RideProgressRow
        label={<Msg startCase m={commonMetrics.elevationLoss} />}
        value={<Elevation short value={trip.elevationLoss} />}
      />
      <hr />
      <RideProgressRow
        label={<Msg startCase m={commonMetrics.averageSpeed} />}
        value={<Speed short value={trip.avgSpeed} />}
      />
      <hr />
      <RideProgressRow label={<Msg m={commonMetrics.maxGrade} />} value={<Grade short value={grade} />} />
      <hr />
      {trip.calories && (
        <Fragment>
          <RideProgressRow label={<Msg startCase m={commonMetrics.calories} />} value={trip.calories} />
          <hr />
        </Fragment>
      )}
      <RideProgressRow label={<Msg m={commonMetrics.movingTime} />} value={durationToHHMMSS(trip.movingTime)} />
      <hr />
      <RideProgressRow
        label={<T id="RideProgress.timeStopped" defaultMessage="Time Stopped" />}
        value={durationToHHMMSS(stoppedTime)}
      />
      <hr />
    </div>
  );
};

// this is such a mess
const durationToHHMMSS = (duration) =>
  `${moment.utc(moment.duration(duration, "seconds").asMilliseconds()).format("HH:mm:ss")}`;

const RideProgressRow = ({ label, value }) => {
  return (
    <div className={styles.rpRow}>
      <span className={styles.rpLabel}>{label}</span>
      <span className={styles.rpValue}>{value}</span>
    </div>
  );
};

const LinkedTRoute = ({ troute }) => {
  const fm = useFormatMessage();
  let header, link, thumb;
  if (troute instanceof Trip) {
    header = <T id="llSidePanel.linkedTrip" defaultMessage="Linked Trip" />;
    link = urls.trips.show(troute.id);
    thumb = <TripThumb trip={troute} size={52} />;
  } else {
    header = <T id="llSidePanel.linkedRoute" defaultMessage="Linked Route" />;
    link = urls.routes.show(troute.id);
    thumb = <RouteThumb route={troute} size={52} />;
  }

  return (
    <div className={styles.bodySection}>
      <h3 className={styles.sectionHeader}>{header}</h3>
      <hr />
      <div className={styles.lrLower}>
        <CoreLink to={link} href={link}>
          <h4>{troute.name}</h4>
          <div className={styles.lrMetrics}>
            <div>{j([troute.locality, troute.administrativeArea])}</div>
            <Distance value={troute.distance}>
              {(m) => <Metric metric={m} label={fm(commonMetrics.distance)} />}
            </Distance>
            <Elevation value={troute.elevationGain}>
              {(m) => <Metric metric={m} label={fm(commonMetrics.elevationGain)} />}
            </Elevation>
          </div>
        </CoreLink>
        {thumb}
      </div>
    </div>
  );
};

const Metric = ({ metric, label }) => (
  <div className={styles.Metric} title={label}>
    <span className={styles.metricValue}>
      {metric.valueToString()}
      {<span className={styles.units}>{metric.short}</span>}
    </span>
  </div>
);

const SignUpsell = () => {
  return (
    <div className={styles.signup}>
      <img src={llUpsell} srcSet={`${llUpsell} 1x, ${llUpsell2x} 2x, ${llUpsell3x} 3x`} alt="women on bikes" />
      <h3>
        <T id="llSidePanel.shareIRT" defaultMessage="Share Your Rides in Real Time" />
      </h3>
      <div className={styles.signupBody}>
        <T
          id="llSidePanel.signupBody"
          defaultMessage="Using our live logging feature, you can share your rides in real time.
      Directly link friends and family to your ride so they can follow along,
      or post a link to your ride directly to social media. The app will read
      comments aloud as they are posted, so your followers can cheer you on from
      anywhere in the world."
        />
      </div>
      <CTA href="/signup">
        <T id="llSidePanel.signUp" defaultMessage="Sign Up" />
      </CTA>
    </div>
  );
};
